/**
 * Enhanced Button component with consistent styling and accessibility
 */
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Button.css';

const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      disabled = false,
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      type = 'button',
      className = '',
      onClick,
      ...props
    },
    ref,
  ) => {
    const baseClasses = 'btn';
    const variantClasses = `btn--${variant}`;
    const sizeClasses = `btn--${size}`;
    const stateClasses = [
      disabled && 'btn--disabled',
      loading && 'btn--loading',
      fullWidth && 'btn--full-width',
    ]
      .filter(Boolean)
      .join(' ');

    const allClasses = [baseClasses, variantClasses, sizeClasses, stateClasses, className]
      .filter(Boolean)
      .join(' ');

    const handleClick = event => {
      if (disabled || loading) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    return (
      <button
        ref={ref}
        type={type}
        className={allClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        aria-disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <span className='btn__spinner' aria-hidden='true'>
            <svg className='btn__spinner-icon' viewBox='0 0 24 24'>
              <circle
                className='btn__spinner-path'
                cx='12'
                cy='12'
                r='10'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeDasharray='31.416'
                strokeDashoffset='31.416'
              />
            </svg>
          </span>
        )}

        {leftIcon && !loading && (
          <span className='btn__icon btn__icon--left' aria-hidden='true'>
            {leftIcon}
          </span>
        )}

        <span className='btn__content'>{children}</span>

        {rightIcon && !loading && (
          <span className='btn__icon btn__icon--right' aria-hidden='true'>
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost', 'danger']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default Button;
